import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"

import { usePermissions } from "contexts/PermissionsContext"

import { PERMISSIONS } from "constants/permissions"

import { AdminBonus2Service } from "services/bonus/AdminBonus2Service"
import { AdminPlayerBonus2, BonusHistory } from "services/bonus/type"
import { gameService } from "services/game/game.service"
import { GameInterface } from "services/game/type"

import { UserInfoHeader } from "../shared/UserInfoHeader"
import { UserPicker } from "../shared/UserPicker"

import { ActivateBonusModal } from "./ActivateBonusModal"
import { AssignBonus } from "./AssignBonus"
import { BonusHistoryModal } from "./BonusHistoryModal"
import { CancelBonusModal } from "./CancelBonusModal"
import { PlayerBonusCard } from "./PlayerBonusCard"

interface PlayerBonusManagerProps {
  tenant: string
  /**
   * Fix 04b — when the manager is embedded in a specific user's admin detail page
   * (the /user "Bonuses" tab), the user is fixed: pass the id (and optionally the
   * username) so the internal UserPicker is hidden and the list is scoped to that user.
   * Absent → standalone bonus2 admin page, which renders the picker as before.
   */
  fixedUserId?: string
  fixedUsername?: string
}

export const PlayerBonusManager: React.FC<PlayerBonusManagerProps> = ({ tenant, fixedUserId, fixedUsername }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const isEmbedded = Boolean(fixedUserId)
  const [userId, setUserId] = useState(fixedUserId ?? "")
  const [username, setUsername] = useState(fixedUsername ?? "")
  const [playerBonuses, setPlayerBonuses] = useState<AdminPlayerBonus2[]>([])
  const [selectedHistory, setSelectedHistory] = useState<{
    playerBonus: AdminPlayerBonus2
    history: BonusHistory[]
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [gamesCache, setGamesCache] = useState<Record<string, GameInterface>>({})
  const { hasAny } = usePermissions()
  const canManage = hasAny([PERMISSIONS.PAGE_BONUS2, PERMISSIONS.BONUS2_PLAYER_MANAGE])

  const [cancelTarget, setCancelTarget] = useState<AdminPlayerBonus2 | null>(null)
  const [activateTarget, setActivateTarget] = useState<AdminPlayerBonus2 | null>(null)
  const [activating, setActivating] = useState(false)
  // Fix 03 — embedded "Add bonus" panel toggle (user-detail tab only).
  const [showAssign, setShowAssign] = useState(false)

  // Keep the locked user in sync when the embedding page swaps the target user.
  useEffect(() => {
    if (fixedUserId !== undefined) setUserId(fixedUserId)
    if (fixedUsername !== undefined) setUsername(fixedUsername)
  }, [fixedUserId, fixedUsername])

  useEffect(() => {
    if (userId) {
      handleSearch()
    } else {
      setPlayerBonuses([])
    }
  }, [userId])

  const handleUserSelect = (selectedUserId: string, selectedUsername: string) => {
    setUserId(selectedUserId)
    setUsername(selectedUsername)
  }

  const handleSearch = async () => {
    if (!userId.trim()) {
      toast.error(t("bonus2.players.toastSelectUser") as string)
      return
    }

    setLoading(true)
    const data = await AdminBonus2Service.getPlayerBonuses(userId.trim(), tenant)
    if (data) {
      setPlayerBonuses(data)
      if (data.length === 0) {
        // Embedded (user detail tab): render an inline empty state, no toast noise.
        if (!isEmbedded)
          toast.info(t("bonus2.players.toastNoBonusesFor", { name: username || (t("bonus2.players.thisUser") as string) }) as string)
      } else {
        await fetchGameDetails(data)
      }
    } else {
      setPlayerBonuses([])
    }
    setLoading(false)
  }

  const fetchGameDetails = async (bonuses: AdminPlayerBonus2[]) => {
    const gameIds = new Set<string>()

    bonuses.forEach((pb) => {
      if (pb.bonus.free_spins_game_ids && pb.bonus.free_spins_game_ids.length > 0) {
        pb.bonus.free_spins_game_ids.forEach((id) => gameIds.add(id))
      }
    })

    if (gameIds.size === 0) return

    console.log("Free spin game IDs from bonus:", Array.from(gameIds))

    try {
      // Fetch specific games by IDs instead of all games
      const gameIdsArray = Array.from(gameIds)
      console.log("Fetching games by IDs:", gameIdsArray)

      const response = await gameService.getGamesByIds({
        gameIds: gameIdsArray,
        includeDisabled: true
      })

      if (response && response.games) {
        const cache: Record<string, GameInterface> = {}
        response.games.forEach((game: GameInterface) => {
          gameIdsArray.forEach((requestedId) => {
            if (
              game.identifier === requestedId ||
              `${game.provider}:${game.identifier}` === requestedId ||
              `${game.gameProvider?.identifier}:${game.identifier}` === requestedId ||
              game.identifier2 === requestedId
            ) {
              cache[requestedId] = game
            }
          })
        })

        setGamesCache(cache)
        console.log("Games cache created:", Object.keys(cache).length, "entries")
        console.log("Found games:", Object.keys(cache))

        // Log missing games
        const missingGames = gameIdsArray.filter((id) => !cache[id])
        if (missingGames.length > 0) {
          console.warn("Missing games:", missingGames)
        }
      }
    } catch (error) {
      console.error("Failed to fetch game details:", error)
    }
  }

  const handleCancel = async (playerBonus: AdminPlayerBonus2) => {
    setCancelTarget(playerBonus)
  }

  const handleConfirmCancel = async (reason: string) => {
    if (!cancelTarget) return

    const success = await AdminBonus2Service.cancelPlayerBonus(cancelTarget, reason, tenant)
    if (success) {
      toast.success(t("bonus2.players.toastCancelled") as string)
      setCancelTarget(null)
      handleSearch() // Refresh
    } else {
      toast.error(t("bonus2.players.toastCancelFailed") as string)
    }
  }

  const handleActivate = (playerBonus: AdminPlayerBonus2) => {
    setActivateTarget(playerBonus)
  }

  const handleConfirmActivate = async (depositAmount: number | null) => {
    if (!activateTarget) return

    setActivating(true)
    try {
      const activated = await AdminBonus2Service.activatePlayerBonus(activateTarget, depositAmount, tenant)

      if (activated) {
        toast.success(t("bonus2.players.toastActivated") as string)
        setActivateTarget(null)
        handleSearch() // Refresh
      } else {
        toast.error(t("bonus2.players.toastActivateFailed") as string)
      }
    } catch (error) {
      toast.error(t("bonus2.players.toastActivateFailed") as string)
    } finally {
      setActivating(false)
    }
  }

  const handleViewHistory = async (playerBonus: AdminPlayerBonus2) => {
    const history = await AdminBonus2Service.getBonusHistory(playerBonus.id, tenant)
    if (history) {
      setSelectedHistory({ playerBonus, history })
    }
  }

  // Iteration-3 Fix 03 / Fix 08 — derive the active/pending ("stacked") set once.
  // "Currently subscribed / active" mirrors the player's FE; the "Stacked Bonuses"
  // section reuses the same set but is only shown when MORE THAN 2 are queued.
  const stackedBonuses = playerBonuses
    .filter((pb) => pb.status === "ACTIVE" || pb.status === "SUBSCRIBED")
    .sort((a, b) => (a.queue_priority ?? 0) - (b.queue_priority ?? 0))
  const currentBonuses = stackedBonuses

  return (
    <div className="space-y-6">
      {/* User Picker — hidden when embedded in a specific user's detail page (Fix 04b). */}
      {!isEmbedded && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">{t("bonus2.players.selectPlayerSearchBy")}</label>
          <UserPicker selectedUserId={userId} onSelect={handleUserSelect} disabled={loading} />
        </div>
      )}

      {/* Iteration-3 Fix 03 — in-tab "Add bonus" (assign) control, pre-scoped to this user.
          Reuses the existing AssignBonus flow (subscribe + activate) with the picker hidden. */}
      {userId && canManage && (
        <div className="space-y-3">
          <button
            onClick={() => setShowAssign((s) => !s)}
            className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
          >
            {showAssign ? "Close" : "+ Add bonus"}
          </button>
          {showAssign && (
            <AssignBonus
              tenant={tenant}
              fixedUserId={userId}
              onAssigned={() => {
                handleSearch()
              }}
            />
          )}
        </div>
      )}

      {/* Loading indicator */}
      {loading && <LoadingIndicator text={t("bonus2.players.loading") as string} />}

      {/* Empty state — embedded mode shows an inline message instead of a toast. */}
      {isEmbedded && !loading && playerBonuses.length === 0 && (
        <div className="text-center text-gray-400 py-8">{t("bonus2.players.noBonusesFoundFor")}</div>
      )}

      {/* Results */}
      {!loading && playerBonuses.length > 0 && (
        <div className="space-y-4">
          {/* User info header */}
          {username && <UserInfoHeader username={username} userId={userId} bonusCount={playerBonuses.length} />}

          {/* Iteration-3 Fix 03 — a focused "Currently subscribed / active" section that
              mirrors what the player sees on their FE (Wallet / My bonuses). */}
          {currentBonuses.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">{t("bonus2.players.currentlySubscribedActive")}</h3>
              {currentBonuses.map((pb) => (
                <PlayerBonusCard
                  key={pb.id}
                  playerBonus={pb}
                  gamesCache={gamesCache}
                  onViewHistory={handleViewHistory}
                  onCancel={canManage ? handleCancel : undefined}
                  onActivate={canManage ? handleActivate : undefined}
                />
              ))}
            </div>
          )}

          {/* Iteration-3 Fix 08 — a "Stacked Bonuses" section listing all active/pending
              (ACTIVE + SUBSCRIBED) bonuses, ordered by queue_priority. Only shown when MORE
              THAN 2 are stacked, per the requirement. */}
          {stackedBonuses.length > 2 && (
            <div className="space-y-4 rounded-lg border border-amber-500/40 p-4">
              <h3 className="text-lg font-semibold text-amber-300">Stacked Bonuses ({stackedBonuses.length})</h3>
              <p className="text-sm text-gray-400">{t("bonus2.players.allActiveAndPending")}</p>
              {stackedBonuses.map((pb) => (
                <PlayerBonusCard
                  key={`stacked-${pb.id}`}
                  playerBonus={pb}
                  gamesCache={gamesCache}
                  onViewHistory={handleViewHistory}
                  onCancel={canManage ? handleCancel : undefined}
                  onActivate={canManage ? handleActivate : undefined}
                />
              ))}
            </div>
          )}

          <h3 className="text-lg font-semibold text-white">{t("bonus2.players.bonusHistory")}</h3>

          {playerBonuses.map((pb) => (
            <PlayerBonusCard
              key={pb.id}
              playerBonus={pb}
              gamesCache={gamesCache}
              onViewHistory={handleViewHistory}
              onCancel={canManage ? handleCancel : undefined}
              onActivate={canManage ? handleActivate : undefined}
            />
          ))}
        </div>
      )}

      {selectedHistory && (
        <BonusHistoryModal
          playerBonus={selectedHistory.playerBonus}
          history={selectedHistory.history}
          onClose={() => setSelectedHistory(null)}
        />
      )}

      {cancelTarget && (
        <CancelBonusModal playerBonus={cancelTarget} onConfirm={handleConfirmCancel} onClose={() => setCancelTarget(null)} />
      )}

      {activateTarget && (
        <ActivateBonusModal
          playerBonus={activateTarget}
          username={username}
          onConfirm={handleConfirmActivate}
          onClose={() => setActivateTarget(null)}
          loading={activating}
        />
      )}
    </div>
  )
}
